import { inject } from "@angular/core";
import { patchState, signalStoreFeature, type, withMethods } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";
import { IUserProfileState } from "./user-profile.state";
import { exhaustMap, pipe, tap } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { UserProfileService } from "../../services/user-profile/user-profile.service";
import { UserProfile } from "../../models/user-profile/user-profile.model";
import { withUserProfileStateMethods } from "./user-profile.methods.state";
import { NotificationService } from "../../services/notification/notification.service";

export const withUserProfileApiMethods = signalStoreFeature(
    { state: type<IUserProfileState>() },
    withUserProfileStateMethods,
    withMethods((store) => {
        const userProfileService = inject(UserProfileService);
        const notificationService = inject(NotificationService);

        const loadProfileByGuid = rxMethod<string>(
            pipe(
                tap(() => patchState(store, { loading: true, error: null })),
                exhaustMap((userProfileGuid) =>
                    userProfileService.getProfileByGuid(userProfileGuid).pipe(
                        tapResponse({
                            next: ({ userProfile }) => store.setUserProfile(userProfile),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const uploadAvatar = rxMethod<{ userProfileGuid: string; file: File }>(
            pipe(
                tap(() => patchState(store, { uploadingAvatar: true, error: null })),
                exhaustMap(({ userProfileGuid, file }) =>
                    userProfileService.uploadAvatar(userProfileGuid, file).pipe(
                        tapResponse({
                            next: ({ userProfile }: { userProfile: UserProfile }) => {
                                store.setUserProfile(userProfile);
                                patchState(store, { uploadingAvatar: false });
                                notificationService.show('success', 'Profile photo updated.');
                            },
                            error: (error: HttpErrorResponse) => {
                                patchState(store, { uploadingAvatar: false });
                                store.setError(error.message);
                                notificationService.notifyError(
                                    'Upload failed',
                                    error.error?.title ?? error.error ?? 'Could not upload profile photo.'
                                );
                            }
                        })
                    )
                )
            )
        );

        return { loadProfileByGuid, uploadAvatar };
    })
);
